import { MapPin, Navigation } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { isCountyLocation } from "@/utils/location";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface LocationSwitcherModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LocationSwitcherModal = ({ open, onOpenChange }: LocationSwitcherModalProps) => {
  const { allLocations, setLocation, detectLocation } = useLocation();

  const handleSelectLocation = (location: any) => {
    setLocation(location);
    onOpenChange(false);
  };

  // Location SEO Consolidation: Filter to county-level locations only
  const countyLocations = allLocations.filter(loc => 
    isCountyLocation({ name: loc.name, county: loc.county })
  );

  // Group towns by county for display (informational only)
  const townsByCounty: Record<string, string[]> = {};
  allLocations.forEach(loc => {
    if (loc.county && loc.county !== loc.name) {
      if (!townsByCounty[loc.county]) {
        townsByCounty[loc.county] = [];
      }
      townsByCounty[loc.county].push(loc.name);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <MapPin className="w-6 h-6 text-primary" />
            Choose Your County
          </DialogTitle>
          <DialogDescription className="text-base">
            Select the county you live in to see local services and get accurate quotes
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {/* Opt-in geolocation button */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                detectLocation();
                onOpenChange(false);
              }}
              className="gap-2"
            >
              <Navigation className="w-4 h-4" />
              Use My Location
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {countyLocations.map((county) => {
              const towns = townsByCounty[county.name] || [];
              return (
                <Card
                  key={county.id}
                  className="group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:border-primary overflow-hidden"
                  onClick={() => handleSelectLocation(county)}
                >
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <h4 className="font-bold text-lg group-hover:text-primary transition-colors">
                        {county.name}
                      </h4>
                    </div>
                    
                    {towns.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Covering: {towns.slice(0, 5).join(', ')}
                        {towns.length > 5 && ` and ${towns.length - 5} more...`}
                      </p>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationSwitcherModal;
